import { Layout } from "@/components/Layout";
import { Link } from "react-router-dom";
import { ROUTE_PATHS } from "@/lib/index";

export default function PolitiqueConfidentialite() {
  return (
    <Layout>
      <div className="min-h-screen bg-white py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <div className="mb-12">
            <Link 
              to={ROUTE_PATHS.HOME}
              className="text-sm text-[#6e6e73] hover:text-[#1d1d1f] transition-colors mb-4 inline-block"
            >
              ← Retour à l'accueil
            </Link>
            <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#1d1d1f' }}>
              Politique de Confidentialité
            </h1>
            <p className="text-lg text-[#6e6e73]">
              Conforme au Règlement Général sur la Protection des Données (RGPD — UE 2016/679)
            </p>
            <p className="text-sm text-[#6e6e73] mt-2">
              Mise à jour : 31 mars 2026
            </p>
          </div>

          <div className="prose prose-lg max-w-none">
            <div className="mb-8 p-6 bg-[#f5f5f7] rounded-lg">
              <p className="text-[#1d1d1f] leading-relaxed">
                La présente politique de confidentialité décrit la manière dont <strong>Camille Vega (SARL, SIRET : 988 977 260 00013)</strong>, située au <strong>60 rue François Premier, 75008 Paris</strong>, collecte, utilise, stocke et protège vos données personnelles dans le cadre de l'utilisation du site internet <strong>camillevega.fr</strong> et des services associés (formations en ligne, coaching individuel).
              </p>
              <p className="text-[#1d1d1f] leading-relaxed mt-4">
                Elle s'applique à toute personne résidant dans l'Union européenne ou dont les données sont traitées par une entité établissant une activité commerciale envers des utilisateurs européens, conformément au RGPD (Règlement UE 2016/679).
              </p>
            </div>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1d1d1f' }}>
                Article 1 — Quelles données sont collectées ?
              </h2>
              <p className="text-[#1d1d1f] leading-relaxed mb-4">
                Vous n'avez aucune obligation de nous communiquer des données personnelles pour accéder à nos contenus gratuits. Certains services (commande, accès espace membre, coaching) nécessitent en revanche la collecte des informations suivantes :
              </p>
              
              <div className="ml-6 space-y-4">
                <div>
                  <h3 className="font-semibold text-[#1d1d1f] mb-2">Données fournies directement par vous :</h3>
                  <ul className="list-disc ml-6 space-y-2 text-[#1d1d1f]">
                    <li><strong>Données d'identification :</strong> nom, prénom, adresse email</li>
                    <li><strong>Données de facturation :</strong> adresse postale, informations de paiement (traitées via Systeme.io — Camille Vega ne stocke pas vos coordonnées bancaires)</li>
                    <li><strong>Données de communication :</strong> messages envoyés via le formulaire de contact ou par email</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-[#1d1d1f] mb-2">Données collectées automatiquement lors de votre visite :</h3>
                  <ul className="list-disc ml-6 space-y-2 text-[#1d1d1f]">
                    <li>Adresse IP (anonymisée dans la mesure du possible)</li>
                    <li>Données de navigation (pages visitées, durée de session) via les cookies essentiels</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-[#1d1d1f] mb-2">Données collectées via des tiers :</h3>
                  <ul className="list-disc ml-6 space-y-2 text-[#1d1d1f]">
                    <li>Données issues de Systeme.io lors de l'inscription ou de l'achat (gestion du tunnel de vente et de l'espace membre)</li>
                    <li>Données issues d'Airtable dans le cadre de la gestion interne des contacts</li>
                  </ul>
                </div>
              </div>

              <p className="text-[#6e6e73] italic mt-4">
                Note : Aucune donnée sensible au sens de l'article 9 du RGPD (origine ethnique, santé, convictions religieuses, etc.) n'est collectée ou traitée.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1d1d1f' }}>
                Article 2 — Comment les données sont-elles collectées ?
              </h2>
              <p className="text-[#1d1d1f] leading-relaxed mb-4">
                Vos données sont collectées selon les modalités suivantes :
              </p>
              <ul className="list-disc ml-6 space-y-2 text-[#1d1d1f]">
                <li><strong>Directement par vous :</strong> lors de votre inscription, de l'achat d'une formation, de la prise de contact ou de l'inscription à la newsletter.</li>
                <li><strong>Automatiquement :</strong> lors de votre navigation sur camillevega.fr, via les cookies strictement nécessaires au fonctionnement du site (voir notre Politique de Cookies).</li>
                <li><strong>Via des prestataires tiers :</strong> Systeme.io (gestion des tunnels de vente et espace membre) et Airtable (gestion des contacts). Ces prestataires agissent en tant que sous-traitants au sens du RGPD et sont soumis à des obligations contractuelles de protection des données.</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1d1d1f' }}>
                Article 3 — Comment utilisons-nous vos données ?
              </h2>
              <p className="text-[#1d1d1f] leading-relaxed mb-4">
                Vos données sont traitées sur la base des fondements juridiques suivants, conformément à l'article 6 du RGPD :
              </p>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-[#d2d2d7] mb-4">
                  <thead>
                    <tr className="bg-[#f5f5f7]">
                      <th className="border border-[#d2d2d7] px-4 py-3 text-left text-[#1d1d1f] font-semibold">Finalité du traitement</th>
                      <th className="border border-[#d2d2d7] px-4 py-3 text-left text-[#1d1d1f] font-semibold">Base juridique</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-[#d2d2d7] px-4 py-3 text-[#1d1d1f]">Traitement et gestion des commandes, accès à l'espace membre</td>
                      <td className="border border-[#d2d2d7] px-4 py-3 text-[#1d1d1f]">Exécution du contrat (art. 6.1.b)</td>
                    </tr>
                    <tr className="bg-[#f5f5f7]">
                      <td className="border border-[#d2d2d7] px-4 py-3 text-[#1d1d1f]">Envoi d'emails liés à votre commande ou à votre suivi</td>
                      <td className="border border-[#d2d2d7] px-4 py-3 text-[#1d1d1f]">Exécution du contrat (art. 6.1.b)</td>
                    </tr>
                    <tr>
                      <td className="border border-[#d2d2d7] px-4 py-3 text-[#1d1d1f]">Envoi de communications marketing et promotionnelles</td>
                      <td className="border border-[#d2d2d7] px-4 py-3 text-[#1d1d1f]">Consentement (art. 6.1.a) — opt-in explicite requis</td>
                    </tr>
                    <tr className="bg-[#f5f5f7]">
                      <td className="border border-[#d2d2d7] px-4 py-3 text-[#1d1d1f]">Gestion des contacts via Airtable</td>
                      <td className="border border-[#d2d2d7] px-4 py-3 text-[#1d1d1f]">Intérêt légitime (art. 6.1.f) — gestion interne</td>
                    </tr>
                    <tr>
                      <td className="border border-[#d2d2d7] px-4 py-3 text-[#1d1d1f]">Sécurité du site, détection de fraudes et d'intrusions</td>
                      <td className="border border-[#d2d2d7] px-4 py-3 text-[#1d1d1f]">Intérêt légitime (art. 6.1.f)</td>
                    </tr>
                    <tr className="bg-[#f5f5f7]">
                      <td className="border border-[#d2d2d7] px-4 py-3 text-[#1d1d1f]">Respect des obligations légales et comptables</td>
                      <td className="border border-[#d2d2d7] px-4 py-3 text-[#1d1d1f]">Obligation légale (art. 6.1.c)</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-[#1d1d1f] leading-relaxed font-semibold">
                Vos données ne sont jamais vendues à des tiers. Elles ne sont partagées qu'avec les sous-traitants listés à l'article 5 et dans les cas expressément prévus par la loi.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1d1d1f' }}>
                Article 4 — Comment sont stockées vos données ?
              </h2>
              <p className="text-[#1d1d1f] leading-relaxed mb-4">
                Vos données sont stockées de manière sécurisée :
              </p>
              <ul className="list-disc ml-6 space-y-2 text-[#1d1d1f] mb-6">
                <li><strong>Siège social du responsable de traitement :</strong> 60 rue François Premier, 75008 Paris</li>
                <li><strong>Infrastructure d'hébergement du site :</strong> Vercel Inc. (serveurs en Europe ou aux États-Unis dans le cadre des garanties contractuelles adéquates)</li>
                <li><strong>Plateforme espace membre :</strong> Systeme.io (serveurs hébergés en conformité avec le RGPD)</li>
                <li><strong>Gestion des contacts :</strong> Airtable (serveurs aux États-Unis — transfert encadré par les clauses contractuelles types de la Commission européenne)</li>
              </ul>

              <h3 className="font-semibold text-[#1d1d1f] mb-3">Durée de conservation :</h3>
              <ul className="list-disc ml-6 space-y-2 text-[#1d1d1f]">
                <li><strong>Données clients (commandes, accès formation) :</strong> 5 ans à compter de la dernière interaction ou de la fin de la relation commerciale</li>
                <li><strong>Données de facturation :</strong> 10 ans conformément aux obligations comptables légales</li>
                <li><strong>Données de navigation (cookies) :</strong> 13 mois maximum</li>
                <li><strong>Données marketing (newsletter) :</strong> jusqu'à désinscription ou 3 ans d'inactivité</li>
              </ul>
              <p className="text-[#6e6e73] italic mt-4">
                Passé le délai de conservation applicable, vos données sont supprimées ou anonymisées de manière sécurisée.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1d1d1f' }}>
                Article 5 — Sous-traitants et tiers destinataires
              </h2>
              <p className="text-[#1d1d1f] leading-relaxed mb-4">
                Camille Vega fait appel aux sous-traitants suivants pour l'exécution de ses services. Chacun d'eux est lié par des obligations contractuelles de protection des données conformes au RGPD :
              </p>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-[#d2d2d7] mb-4">
                  <thead>
                    <tr className="bg-[#f5f5f7]">
                      <th className="border border-[#d2d2d7] px-4 py-3 text-left text-[#1d1d1f] font-semibold">Sous-traitant</th>
                      <th className="border border-[#d2d2d7] px-4 py-3 text-left text-[#1d1d1f] font-semibold">Pays</th>
                      <th className="border border-[#d2d2d7] px-4 py-3 text-left text-[#1d1d1f] font-semibold">Rôle</th>
                      <th className="border border-[#d2d2d7] px-4 py-3 text-left text-[#1d1d1f] font-semibold">Site</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-[#d2d2d7] px-4 py-3 text-[#1d1d1f]">Systeme.io SAS</td>
                      <td className="border border-[#d2d2d7] px-4 py-3 text-[#1d1d1f]">France / UE</td>
                      <td className="border border-[#d2d2d7] px-4 py-3 text-[#1d1d1f]">Tunnel de vente, espace membre, paiements</td>
                      <td className="border border-[#d2d2d7] px-4 py-3 text-[#1d1d1f]">systeme.io</td>
                    </tr>
                    <tr className="bg-[#f5f5f7]">
                      <td className="border border-[#d2d2d7] px-4 py-3 text-[#1d1d1f]">Vercel Inc.</td>
                      <td className="border border-[#d2d2d7] px-4 py-3 text-[#1d1d1f]">États-Unis</td>
                      <td className="border border-[#d2d2d7] px-4 py-3 text-[#1d1d1f]">Hébergement du site</td>
                      <td className="border border-[#d2d2d7] px-4 py-3 text-[#1d1d1f]">vercel.com</td>
                    </tr>
                    <tr>
                      <td className="border border-[#d2d2d7] px-4 py-3 text-[#1d1d1f]">Airtable Inc.</td>
                      <td className="border border-[#d2d2d7] px-4 py-3 text-[#1d1d1f]">États-Unis</td>
                      <td className="border border-[#d2d2d7] px-4 py-3 text-[#1d1d1f]">Gestion interne des contacts</td>
                      <td className="border border-[#d2d2d7] px-4 py-3 text-[#1d1d1f]">airtable.com</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-[#1d1d1f] leading-relaxed">
                Pour les transferts hors UE (Vercel, Airtable), des garanties adéquates sont mises en place conformément à l'article 46 du RGPD (clauses contractuelles types de la Commission européenne et/ou certifications adéquates).
              </p>
              <p className="text-[#1d1d1f] leading-relaxed font-semibold mt-4">
                Vos données ne sont jamais cédées, vendues ou échangées à des fins commerciales avec des tiers non mentionnés dans le présent document.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1d1d1f' }}>
                Article 6 — Communications marketing
              </h2>
              <p className="text-[#1d1d1f] leading-relaxed mb-4">
                Avec votre consentement explicite (opt-in), Camille Vega peut vous adresser des communications commerciales concernant ses formations, ses contenus et ses services.
              </p>
              <p className="text-[#1d1d1f] leading-relaxed mb-4">
                Vous pouvez retirer votre consentement à tout moment :
              </p>
              <ul className="list-disc ml-6 space-y-2 text-[#1d1d1f] mb-4">
                <li>En cliquant sur le lien de désinscription présent dans chaque email</li>
                <li>En adressant une demande à : <a href="mailto:camille@camillevega.com" className="text-[#C9793A] hover:underline">camille@camillevega.com</a></li>
              </ul>
              <p className="text-[#6e6e73] italic">
                Le retrait du consentement ne remet pas en cause la liceité des traitements effectués avant ce retrait.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1d1d1f' }}>
                Article 7 — Sécurité des données
              </h2>
              <p className="text-[#1d1d1f] leading-relaxed mb-4">
                Camille Vega met en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données personnelles contre toute perte, accès non autorisé, divulgation ou altération, notamment :
              </p>
              <ul className="list-disc ml-6 space-y-2 text-[#1d1d1f] mb-4">
                <li>Chiffrement des données en transit (HTTPS / TLS)</li>
                <li>Accès restreint aux données selon le principe du moindre privilège</li>
                <li>Utilisation de plateformes conformes aux normes de sécurité en vigueur (Vercel, Systeme.io, Airtable)</li>
                <li>Révision périodique des procédures et des systèmes</li>
              </ul>
              <p className="text-[#1d1d1f] leading-relaxed">
                En cas de violation de données susceptible d'engendrer un risque pour vos droits et libertés, Camille Vega s'engage à notifier la CNIL dans les 72 heures conformément à l'article 33 du RGPD, et à vous en informer si le risque est élevé.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1d1d1f' }}>
                Article 8 — Vos droits sur vos données
              </h2>
              <p className="text-[#1d1d1f] leading-relaxed mb-4">
                Conformément au RGPD (chapitres III et IV), vous disposez des droits suivants concernant vos données personnelles :
              </p>
              
              <div className="space-y-4 mb-6">
                <div className="p-4 bg-[#f5f5f7] rounded-lg">
                  <h3 className="font-semibold text-[#1d1d1f] mb-2">Droit d'accès</h3>
                  <p className="text-[#1d1d1f]">Obtenir une copie des données personnelles que nous détenons sur vous.</p>
                </div>
                <div className="p-4 bg-[#f5f5f7] rounded-lg">
                  <h3 className="font-semibold text-[#1d1d1f] mb-2">Droit de rectification</h3>
                  <p className="text-[#1d1d1f]">Faire corriger ou compléter des données inexactes ou incomplètes.</p>
                </div>
                <div className="p-4 bg-[#f5f5f7] rounded-lg">
                  <h3 className="font-semibold text-[#1d1d1f] mb-2">Droit à l'effacement</h3>
                  <p className="text-[#1d1d1f]">Demander la suppression de vos données, sous certaines conditions légales.</p>
                </div>
                <div className="p-4 bg-[#f5f5f7] rounded-lg">
                  <h3 className="font-semibold text-[#1d1d1f] mb-2">Droit à la limitation</h3>
                  <p className="text-[#1d1d1f]">Demander la suspension temporaire du traitement de vos données.</p>
                </div>
                <div className="p-4 bg-[#f5f5f7] rounded-lg">
                  <h3 className="font-semibold text-[#1d1d1f] mb-2">Droit d'opposition</h3>
                  <p className="text-[#1d1d1f]">Vous opposer au traitement de vos données, notamment à des fins de prospection.</p>
                </div>
                <div className="p-4 bg-[#f5f5f7] rounded-lg">
                  <h3 className="font-semibold text-[#1d1d1f] mb-2">Droit à la portabilité</h3>
                  <p className="text-[#1d1d1f]">Recevoir vos données dans un format structuré ou les faire transférer à un tiers.</p>
                </div>
                <div className="p-4 bg-[#f5f5f7] rounded-lg">
                  <h3 className="font-semibold text-[#1d1d1f] mb-2">Retrait du consentement</h3>
                  <p className="text-[#1d1d1f]">Retirer à tout moment un consentement préalablement donné, sans effet rétroactif.</p>
                </div>
                <div className="p-4 bg-[#f5f5f7] rounded-lg">
                  <h3 className="font-semibold text-[#1d1d1f] mb-2">Droit de réclamation</h3>
                  <p className="text-[#1d1d1f]">Introduire une réclamation auprès de l'autorité de contrôle compétente (CNIL).</p>
                </div>
              </div>

              <div className="p-6 bg-[#C9793A] bg-opacity-10 border-l-4 border-[#C9793A] rounded">
                <h3 className="font-semibold text-[#1d1d1f] mb-3">Pour exercer vos droits :</h3>
                <p className="text-[#1d1d1f] mb-2">
                  Adressez votre demande par email à : <a href="mailto:camille@camillevega.com" className="text-[#C9793A] hover:underline font-semibold">camille@camillevega.com</a>
                </p>
                <p className="text-[#1d1d1f] text-sm">
                  Nous nous engageons à vous répondre dans un délai d'un mois à compter de la réception de votre demande (délai pouvant être prolongé de deux mois supplémentaires en cas de complexité, après information préalable).
                </p>
                <p className="text-[#6e6e73] text-sm mt-2 italic">
                  Une pièce d'identité pourra vous être demandée pour vérifier votre identité.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1d1d1f' }}>
                Article 9 — Liens vers des sites tiers
              </h2>
              <p className="text-[#1d1d1f] leading-relaxed mb-4">
                Le site camillevega.fr peut contenir des liens hypertextes vers des sites internet tiers. La présente politique de confidentialité ne s'applique qu'à camillevega.fr.
              </p>
              <p className="text-[#1d1d1f] leading-relaxed">
                Camille Vega n'exerce aucun contrôle sur les pratiques de ces sites en matière de données personnelles et décline toute responsabilité à ce titre. Nous vous invitons à consulter les politiques de confidentialité de chaque site que vous visitez.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1d1d1f' }}>
                Article 10 — Cookies
              </h2>
              <p className="text-[#1d1d1f] leading-relaxed mb-4">
                L'utilisation des cookies sur camillevega.fr est détaillée dans notre Politique de Cookies, accessible depuis le pied de page du site et depuis les documents légaux.
              </p>
              <p className="text-[#1d1d1f] leading-relaxed">
                En résumé : seuls les cookies strictement nécessaires au fonctionnement du site sont déposés sans consentement. Tout cookie analytique ou marketing supplémentaire nécessite votre accord préalable explicite.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1d1d1f' }}>
                Article 11 — Autorité de contrôle
              </h2>
              <p className="text-[#1d1d1f] leading-relaxed mb-4">
                Si vous estimez que le traitement de vos données personnelles constitue une violation du RGPD, vous avez le droit d'introduire une réclamation auprès de l'autorité de protection des données compétente :
              </p>
              
              <div className="p-6 bg-[#f5f5f7] rounded-lg">
                <h3 className="font-semibold text-[#1d1d1f] mb-3">CNIL — Commission Nationale de l'Informatique et des Libertés</h3>
                <p className="text-[#1d1d1f]">3 Place de Fontenoy – TSA 80715, 75334 Paris Cedex 07</p>
                <p className="text-[#1d1d1f]">Site : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#C9793A] hover:underline">www.cnil.fr</a></p>
                <p className="text-[#1d1d1f]">Tél. : +33 (0)1 53 73 22 22</p>
              </div>

              <p className="text-[#6e6e73] italic mt-4">
                Les utilisateurs résidant dans un autre État membre de l'UE peuvent également saisir l'autorité de protection des données de leur pays de résidence.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1d1d1f' }}>
                Article 12 — Mises à jour de cette politique
              </h2>
              <p className="text-[#1d1d1f] leading-relaxed mb-4">
                Camille Vega se réserve le droit de modifier la présente politique de confidentialité à tout moment, notamment pour se conformer à de nouvelles obligations légales ou à des évolutions de ses services.
              </p>
              <p className="text-[#1d1d1f] leading-relaxed mb-4">
                En cas de modification substantielle affectant vos droits ou la manière dont nous utilisons vos données, vous en serez informé par email ou par un avis visible sur le site, avant l'entrée en vigueur des changements.
              </p>
              <p className="text-[#1d1d1f] leading-relaxed">
                La date de dernière mise à jour figure en haut de ce document. Nous vous encourageons à la consulter régulièrement.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1d1d1f' }}>
                Article 13 — Nous contacter
              </h2>
              <p className="text-[#1d1d1f] leading-relaxed mb-4">
                Pour toute question, demande d'exercice de droits ou préoccupation relative à la protection de vos données personnelles :
              </p>
              
              <div className="p-6 bg-[#f5f5f7] rounded-lg">
                <h3 className="font-semibold text-[#1d1d1f] mb-3">Camille Vega — SARL</h3>
                <p className="text-[#1d1d1f]">Responsable du traitement</p>
                <p className="text-[#1d1d1f]">60 rue François Premier, 75008 Paris</p>
                <p className="text-[#1d1d1f]">SIRET : 988 977 260 00013</p>
                <p className="text-[#1d1d1f] mt-3">
                  Email : <a href="mailto:camille@camillevega.com" className="text-[#C9793A] hover:underline font-semibold">camille@camillevega.com</a>
                </p>
                <p className="text-[#1d1d1f]">
                  Site : <a href="https://camillevega.fr" target="_blank" rel="noopener noreferrer" className="text-[#C9793A] hover:underline">https://camillevega.fr</a>
                </p>
              </div>
            </section>

            <div className="mt-12 p-6 bg-[#f5f5f7] border-l-4 border-[#C9793A] rounded">
              <p className="text-[#1d1d1f] text-center font-semibold">
                Camille Vega — SARL — Shut Up & Close
              </p>
              <p className="text-[#1d1d1f] text-center text-sm mt-2">
                60 rue François Premier, 75008 Paris · SIRET 988 977 260 00013
              </p>
              <p className="text-[#1d1d1f] text-center text-sm">
                <a href="mailto:camille@camillevega.com" className="text-[#C9793A] hover:underline">camille@camillevega.com</a> · <a href="https://camillevega.fr" target="_blank" rel="noopener noreferrer" className="text-[#C9793A] hover:underline">camillevega.fr</a>
              </p>
              <p className="text-[#6e6e73] text-center text-xs mt-4 italic">
                À faire valider par un juriste avant mise en ligne officielle.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
